import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Ota } from 'frontend-utils/models';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type {
  Carate,
  Day,
  Occupancy,
  Rate,
  Restriction,
  RestrictionAction,
  RestrictionRecommendation,
} from 'lumen/models';
import type PriceAction from 'lumen/models/price-action';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import type { RestrictionModelContextsType } from 'lumen/utils/constants/channel-pricing-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import { OD_SETTINGS_KEYS } from 'lumen/utils/constants/overview-dashboard-constants';
import {
  type OpenRoomPricingFixedCategory,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';

const SIGNIFICANT_DIFFERENCE = 0.005;

interface OverriddenValueOptions {
  effectiveValue: number | null;
  hasFailedPush: boolean;
  overrideValue: number | null | undefined;
}

export interface PeriodRate {
  best_flex: boolean;
  cancellable: boolean;
  cancellation: boolean;
  cemaCategory: OpenRoomPricingFixedCategory;
  city_tax: number;
  city_tax_incl: boolean;
  comparisonRates?: PeriodComparisonRate[];
  comparison_rates?: PeriodComparisonRate[];
  currency: SupportedCurrencies;
  difference_with_baserate: number;
  extract_datetime: string;
  hotelinfo: number;
  id: string;
  is_baserate: boolean;
  is_out_of_sync: boolean;
  is_wholesale_rate: boolean;
  issue_type: number;
  labels: string[];
  max_persons: number;
  // Added duplicated property name in camelCase for compatibility with ember-data in the future
  mealtypeIncluded: MealTypeValues;
  mealtype_included: MealTypeValues;
  membershiptype: MembershipType;
  message: string;
  ota: string;
  other_taxes: number;
  other_taxes_incl: boolean;
  period: string;
  platform: number;
  pos: string;
  position_to_baserate: number;
  rate_code: string;
  rate_name: string;
  roomName: string;
  room_name: string;
  room_type: string;
  shop_city_tax_incl: boolean;
  shop_currency: string;
  shop_other_taxes_incl: boolean;
  shop_value: number;
  shop_vat_incl: boolean;
  source_id: string;
  value: number;
  vat: number;
  vat_incl: boolean;
}

export interface PeriodComparisonRate extends PeriodRate {
  compare_messages: string[] | null;
  compare_set_id: number;
  difference_absolute: number;
  difference_relative: number;
  from_date: string;
  outlier_score: number;
}

export interface PeriodParity {
  best_flex: boolean;
  cancellable: boolean;
  cancellation: boolean;
  channel: string;
  city_tax: number;
  city_tax_incl: boolean;
  currency: SupportedCurrencies;
  difference_with_baserate: number;
  extract_datetime: string;
  hotelinfo: number;
  id: string;
  is_baserate: boolean;
  is_out_of_sync: boolean;
  is_wholesale_rate: boolean;
  issue_type: number;
  labels: string[] | null;
  max_persons: number;
  // Added duplicated property name in camelCase for compatibility with ember-data in the future
  mealtypeIncluded: MealTypeValues;
  mealtype_included: MealTypeValues;
  membershiptype: MembershipType;
  message: string;
  ota: string;
  other_taxes: number;
  other_taxes_incl: boolean;
  period: string;
  platform: number;
  pos: string;
  position_to_baserate: number;
  rate_code: string;
  rate_name: string;
  room_name: string;
  room_type: string;
  shop_city_tax_incl: boolean;
  shop_currency: string;
  shop_other_taxes_incl: boolean;
  shop_value: number;
  shop_vat_incl: boolean;
  source_id: string;
  unmapped_channel: string;
  value: number;
  vat: number;
  vat_incl: boolean;
}

export default class Period extends Model {
  [Type] = 'period' as const;

  @belongsTo('day', { async: false, inverse: 'periods' })
  declare day: Day;

  @belongsTo('occupancy', { async: false, inverse: 'period' })
  declare occupancy: Occupancy;

  /** BE Team granted that only one price-action is returned for a period */
  @belongsTo('price-action', { async: false, inverse: 'period' })
  declare priceAction: PriceAction;

  @hasMany('carate', { async: false, inverse: 'period' })
  declare carates: HasMany<Carate>;

  @hasMany('rate', { async: false, inverse: 'period' })
  declare ratesModel: HasMany<Rate>;

  @hasMany('restriction-action', { async: false, inverse: 'period' })
  declare restrictionActions: HasMany<RestrictionAction>;

  @hasMany('restriction-recommendation', { async: false, inverse: 'period' })
  declare restrictionRecommendations: HasMany<RestrictionRecommendation>;

  @hasMany('restriction', { async: false, inverse: 'period' })
  declare restrictions: HasMany<Restriction>;

  @attr('hoteldate') declare extractDate: NonNullable<HoteldateTransformReturn>;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('number') declare leadtime: number;
  @attr('number') declare los: number;
  @attr() declare parities?: PeriodParity[];
  @attr('string') declare pos: string;
  @attr() declare rankings: unknown;

  // For the `meta=nested` case
  @attr() declare rates: Record<string, [PeriodRate, ...PeriodRate[]]>;

  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkChange() {
    return this.day.occupancy?.benchmarkChangeVersusPercentage(
      this.referenceOccupancy,
    );
  }

  get competitorCarates(): Carate[] {
    return this.carates.filter(
      (carate) =>
        carate.hotelinfo.id !== this.hotelAndMarketService.hotel.hotelinfo.id,
    );
  }

  get competitorRates(): Rate[] {
    return this.ratesModel.filter(
      (rate) =>
        rate.hotelinfo.id !== this.hotelAndMarketService.hotel.hotelinfo.id,
    );
  }

  /* Which level's MinLOS the BE will actually push for the Deep Discount rate group.
  Read from the same record the value comes from: falling back separately could label a
  recommended value with the current restriction's precedence, or vice versa. */
  get deepDiscountMinLosAppliedContext(): RestrictionModelContextsType | null {
    return this.deepDiscountMinLosSource?.minLosAppliedContext ?? null;
  }

  get deepDiscountMinLosExceptionAction(): RestrictionAction | undefined {
    // Restriction actions can outlive their restriction (e.g. the Deep Discount group is deleted after a push), so gate on a live restriction.
    if (!this.deepDiscountRestriction) {
      return undefined;
    }

    return this.restrictionActions.find(
      (action) => action.hasMinLosException && action.isDeepDiscountRateGroup,
    );
  }

  /* The record supplying the Deep Discount MinLOS: the recommendation when it has one,
  otherwise what was last pushed. */
  private get deepDiscountMinLosSource():
    | Restriction
    | RestrictionRecommendation
    | undefined {
    return this.deepDiscountRestrictionRecommendation?.minLosValue != null
      ? this.deepDiscountRestrictionRecommendation
      : this.deepDiscountRestriction;
  }

  get deepDiscountRestriction(): Restriction | undefined {
    return this.restrictions.find(
      (restriction) => restriction.isDeepDiscountRateGroup,
    );
  }

  get deepDiscountRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.restrictionRecommendations.find(
      (recommendation) => recommendation.isDeepDiscountRateGroup,
    );
  }

  get effectiveDeepDiscountMinLos(): number | null {
    return this.deepDiscountMinLosSource?.minLosValue ?? null;
  }

  get effectiveDeepDiscountMinLosWithOverride(): number | null {
    return this.resolveOverriddenValue({
      effectiveValue: this.effectiveDeepDiscountMinLos,
      hasFailedPush:
        this.hasFailedDeepDiscountMinLos &&
        this.shouldShowStayRestrictionException,
      overrideValue: this.deepDiscountRestriction?.minLosOverride?.value,
    });
  }

  get effectiveHotelMinLos(): number | null {
    return (
      this.hotelLevelRestrictionRecommendation?.minLosValue ??
      this.hotelLevelRestriction?.minLosValue ??
      null
    );
  }

  get effectiveHotelMinLosWithOverride(): number | null {
    return this.resolveOverriddenValue({
      effectiveValue: this.effectiveHotelMinLos,
      hasFailedPush:
        this.hasFailedHotelMinLos && this.shouldShowStayRestrictionException,
      overrideValue: this.hotelLevelRestriction?.minLosOverride?.value,
    });
  }

  get effectiveOverbooking(): number | null {
    return (
      this.hotelLevelRestrictionRecommendation?.overbookingValue ??
      this.hotelLevelRestriction?.overbookingValue ??
      null
    );
  }

  get effectiveOverbookingWithOverride(): number | null {
    return this.resolveOverriddenValue({
      effectiveValue: this.effectiveOverbooking,
      hasFailedPush: this.shouldShowOverbookingException,
      overrideValue: this.hotelLevelRestriction?.overbookingOverride?.value,
    });
  }

  get failedDeepDiscountMinLosValue(): number | null {
    return this.deepDiscountMinLosExceptionAction?.minLosAttemptedValue ?? null;
  }

  get failedHotelMinLosValue(): number | null {
    return this.hotelMinLosExceptionAction?.minLosAttemptedValue ?? null;
  }

  get failedOverbookingValue(): number | null {
    return (
      this.overbookingExceptionRestrictionAction?.overbookingAttemptedValue ??
      null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    return this.extractDate.format('YYYY-MM-DD');
  }

  // Used for coupling this year OTB with last year OTB data
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDateLastYear() {
    return this.extractDate.subtract(52, 'week').format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOwnHotelRate() {
    return this.hotelAndMarketService.formatRate(
      this.ownHotelRate,
      true,
      false,
    );
  }

  /* True when any restriction (hotel or rate group) carries an override. Derived
     from the restriction data, so the table-header indicator picks up a new
     override type automatically. */
  get hasAnyRestrictionOverride(): boolean {
    return this.restrictions.some((restriction) => restriction.hasOverride);
  }

  get hasDeepDiscountMinLosActionInProgress(): boolean {
    return this.restrictionActions.some(
      (action) =>
        action.isBeingPushed &&
        action.isDeepDiscountRateGroup &&
        action.hasMinLos,
    );
  }

  get hasDeepDiscountMinLosOverride(): boolean {
    // COA-3693 TODO: remove once COA-3687 is resolved.
    // BE still keeps the failed override active when a push fails.
    // Treat it as absent so the override indicator hides and no stale override is deleted.
    if (
      this.hasFailedDeepDiscountMinLos &&
      this.shouldShowStayRestrictionException
    ) {
      return false;
    }
    return this.deepDiscountRestriction?.minLosOverride != null;
  }

  get hasDemandData(): boolean {
    if (this.day?.demand) {
      return !this.day.demand.isOutOfRange;
    }
    return false;
  }

  get hasFailedDeepDiscountMinLos(): boolean {
    return this.failedDeepDiscountMinLosValue != null;
  }

  get hasFailedHotelMinLos(): boolean {
    return this.failedHotelMinLosValue != null;
  }

  get hasHotelMinLosActionInProgress(): boolean {
    return this.restrictionActions.some(
      (action) =>
        action.isBeingPushed && action.isHotelLevel && action.hasMinLos,
    );
  }

  get hasHotelMinLosOverride(): boolean {
    // COA-3693 TODO: remove once COA-3687 is resolved.
    // BE still keeps the failed override active when a push fails.
    // Treat it as absent so the override indicator hides and no stale override is deleted.
    if (this.hasFailedHotelMinLos && this.shouldShowStayRestrictionException) {
      return false;
    }
    return this.hotelLevelRestriction?.minLosOverride != null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOTBData() {
    // Used in the rates table to determine to show the OTB column or not
    return this.day?.occupancy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOTBShortTermRentalAlert() {
    const rentalOccupancyChange = this.getRentalOccupancyChange();
    return (
      this.overviewSettings[
        OD_SETTINGS_KEYS.short_term_rental_otb_pickup_opt_in
      ] &&
      rentalOccupancyChange !== undefined &&
      Math.abs(rentalOccupancyChange) >=
        this.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_otb_pickup_threshold
        ]
    );
  }

  get hasOverbookingActionInProgress(): boolean {
    return this.restrictionActions
      .filter((action) => action.isBeingPushed)
      .some((action) =>
        action.values?.some(
          (value) => value.type === RESTRICTION_TYPES.OVERBOOKING,
        ),
      );
  }

  get hasOverbookingOverride(): boolean {
    return this.hotelLevelRestriction?.overbookingOverride != null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasParityIssues() {
    if (this.parities) {
      const parityOtaIds = this.hotelAndMarketService.hotel?.otasParity.map(
        (ota) => ota.id,
      );
      const { parities } = this;
      return parities.some(
        (parity) =>
          parityOtaIds.indexOf(parity.ota) && parity.position_to_baserate < 0,
      );
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRateShortTermRentalAlert() {
    const rentalRateChange = this.getRentalRateChange();
    return (
      this.overviewSettings[
        OD_SETTINGS_KEYS.short_term_rental_rate_pickup_opt_in
      ] &&
      rentalRateChange !== undefined &&
      Math.abs(rentalRateChange) >=
        this.overviewSettings[
          OD_SETTINGS_KEYS.short_term_rental_rate_pickup_threshold
        ]
    );
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.restrictions.find((restriction) => restriction.isHotelLevel);
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.restrictionRecommendations.find(
      (recommendation) => recommendation.isHotelLevel,
    );
  }

  get hotelMinLosExceptionAction(): RestrictionAction | undefined {
    // Restriction actions can outlive their restriction. A hotel-level restriction can carry only
    // overbooking, so gate on a live MinLOS restriction specifically, not just any hotel restriction.
    if (!this.hotelLevelRestriction?.minLosRestrictionValueObject) {
      return undefined;
    }

    return this.restrictionActions.find(
      (action) => action.hasMinLosException && action.isHotelLevel,
    );
  }

  get isCurrentOrFutureDay(): boolean {
    return this.day.date.isSameOrAfter(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChange() {
    return this.day.occupancy?.occupancyChangeVersusPercentage(
      this.referenceOccupancy,
    );
  }

  get overbookingExceptionRestrictionAction(): RestrictionAction | undefined {
    const overbookingRestriction = this.restrictions.find(
      (restriction) => restriction.overbookingRestrictionValueObject,
    );

    // Restriction actions can outlive their restriction, so gate on a live overbooking restriction.
    if (!overbookingRestriction) {
      return undefined;
    }

    return this.restrictionActions.find(
      (action) => action.hasOverbookingException,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get overviewSettings() {
    return this.hotelAndMarketService.hotel?.overviewSettings;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownCaratesGroupedByCemaCategory() {
    return this.ownHotelCarates.reduce(
      (obj, item: Carate) => {
        const { cemaCategory } = item;

        if (!obj[cemaCategory]) {
          obj[cemaCategory] = [];
        }
        obj[cemaCategory].push(item);
        return obj;
      },
      {} as Record<OpenRoomPricingFixedCategory, Carate[]>,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelCarates() {
    return this.carates.filter(
      (carate) =>
        carate.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelRate() {
    const periodRates = this.rates[
      // @ts-expect-error: Type 'null' cannot be used as an index type.
      this.hotelAndMarketService.hotel.hotelinfo.id
    ] as PeriodRate[];
    return periodRates[0] as PeriodRate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelRates() {
    return this.rates[
      // @ts-expect-error: Type 'null' cannot be used as an index type.
      this.hotelAndMarketService.hotel.hotelinfo.id
    ] as PeriodRate[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paritiesDict() {
    return this.createParityDict((ota, parity) => ota.id === parity.ota);
  }

  /*
   * For the POS page the parities should be processed differently (else everything would end up in other because it are Trivago rates)
   * Note: we currently still store all non major OTAs in an array.
   * An object would be easier for lookups in the POS table cells, but the channel property we would need to use as key can contain dots :(
   * => so for now we need to loop the others array every time
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paritiesPOSDict() {
    return this.createParityDict((ota, parity) => ota.id === parity.channel);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get predictedOccupancyChange() {
    return this.day.predictedOccupancy?.occupancyChangeVersusPercentage(
      this.referencePredictedOccupancy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceChangeDays() {
    const monthAfterNextMonth = this.hotelAndMarketService
      .getLocalTime()
      .startOf('month')
      .add(2, 'month');

    if (this.day.date.isSameOrAfter(monthAfterNextMonth, 'day')) {
      return 7;
    }

    return 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceChangeText() {
    if (
      this.hasOTBData &&
      (this.showOccupancyChange || this.showMarketBenchmarkChange)
    ) {
      return `${this.intl.t('rates.changes.change_tooltip')} ${this.intl.t(
        'general.daysago',
        {
          count: this.referenceChangeDays,
        },
      )}`;
    }

    // Show "No change vs X day(s) ago" when there are no changes
    return `${this.intl.t('ranking.no_change')} ${this.intl.t(
      'general.daysago',
      {
        count: this.referenceChangeDays,
      },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceOccupancy() {
    const referenceExtractDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(this.referenceChangeDays, 'day')
      .format('YYYY-MM-DD');

    return this.day.occupancy?.occupancyChangeForExtractDate(
      referenceExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePredictedOccupancy() {
    const referenceExtractDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(this.referenceChangeDays, 'day')
      .format('YYYY-MM-DD');

    return this.day.predictedOccupancy?.occupancyChangeForExtractDate(
      referenceExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalFormmattedRateValue() {
    return this.rentalRateForCurrentDay?.formattedValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOccupancyForCurrentDay() {
    return this.day?.rentalOccupancies?.[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalRateForCurrentDay() {
    return this.day?.rentalRates?.[0];
  }

  get shouldShowOverbookingException(): boolean {
    if (!this.isCurrentOrFutureDay) {
      return false;
    }

    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_OVERBOOKING_FAILURE,
      )
    ) {
      return false;
    }

    return this.overbookingExceptionRestrictionAction != null;
  }

  get shouldShowStayRestrictionException(): boolean {
    if (!this.isCurrentOrFutureDay) {
      return false;
    }

    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_MINLOS_FAILURE,
      )
    ) {
      return false;
    }

    return this.hasFailedHotelMinLos || this.hasFailedDeepDiscountMinLos;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMarketBenchmark() {
    return this.access.hasFeature('otb.market_benchmark');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMarketBenchmarkChange() {
    if (
      !this.showOTBChanges ||
      this.benchmarkChange === null ||
      this.benchmarkChange === undefined ||
      this.day.occupancy?.isBenchmarkMissing
    ) {
      return false;
    }

    // Check for significant change (see arrow-change-value.hbs)
    return (
      this.benchmarkChange >= SIGNIFICANT_DIFFERENCE ||
      this.benchmarkChange <= -SIGNIFICANT_DIFFERENCE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOccupancyChange() {
    if (
      !this.showOTBChanges ||
      this.occupancyChange === null ||
      this.occupancyChange === undefined
    ) {
      return false;
    }

    // Check for significant change (see PercentageChangeValue)
    return (
      this.occupancyChange >= SIGNIFICANT_DIFFERENCE ||
      this.occupancyChange <= -SIGNIFICANT_DIFFERENCE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOTBChanges() {
    return this.access.hasFeature('pms.otb.view') && this.isCurrentOrFutureDay;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPredictedOccupancyChange() {
    // Re-using the showOTBChanges property that's also used for the regular OTB as currently it's under the same feature flag
    if (
      !this.showOTBChanges ||
      this.predictedOccupancyChange === null ||
      this.predictedOccupancyChange === undefined
    ) {
      return false;
    }

    // Don't show Predicted Occupancy changes if the current forecasted value is > 100%
    if (this.day.predictedOccupancy?.isPredictedOccupancyPercentageCapped) {
      return false;
    }

    // Check for significant change (see PercentageChangeValue)
    return (
      this.predictedOccupancyChange >= SIGNIFICANT_DIFFERENCE ||
      this.predictedOccupancyChange <= -SIGNIFICANT_DIFFERENCE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showReferenceChangeText() {
    return this.showOTBChanges;
  }

  /**
   * Creates a dict from the parities array
   * Properties are major OTAs (determined by the otaCompare function)
   * Unclassified OTAs will be put in an 'others' property
   * @param  { function } otaCompare : will determine if the ota can be used as a property in the dict, on failing the check it will be put in others
   * @return { object }              : a mapping from ota to parity object, 'others' property groups unclassified parities as an array
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createParityDict(otaCompare: (ota: Ota, parity: PeriodParity) => boolean) {
    if (this.parities) {
      const otas = this.ota.getAllOTAs();
      const { parities } = this;
      const paritiesDict: { [key: string]: unknown; others: unknown[] } = {
        others: [],
      };
      parities.forEach((parity) => {
        let found = false;
        otas.forEach((ota) => {
          if (otaCompare(ota, parity)) {
            found = true;
            if (ota.isMajor) {
              // @ts-expect-error: Type 'null' cannot be used as an index type.
              paritiesDict[ota.id] = parity;
            } else {
              paritiesDict.others.push(parity);
            }
          }
        });
        if (!found) {
          paritiesDict.others.push(parity);
        }
      });
      return paritiesDict;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getBenchmarkChange(referenceChangeDays = this.referenceChangeDays) {
    if (!this.day.occupancy) {
      return null;
    }

    const referenceExtractDate =
      this.getReferenceExtractDate(referenceChangeDays);
    const referenceOccupancy =
      this.day.occupancy.occupancyChangeForExtractDate(referenceExtractDate);
    return this.day.occupancy.benchmarkChangeVersusPercentage(
      referenceOccupancy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCompareRate(referenceChangeDays = this.referenceChangeDays) {
    const referenceExtractDate =
      this.getReferenceExtractDate(referenceChangeDays);
    return this.ownHotelRate.comparison_rates?.find((rate) =>
      rate.extract_datetime.startsWith(referenceExtractDate),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOccupancyChange(referenceChangeDays = this.referenceChangeDays) {
    if (!this.day.occupancy) {
      return null;
    }

    const referenceExtractDate =
      this.getReferenceExtractDate(referenceChangeDays);
    const referenceOccupancy =
      this.day.occupancy.occupancyChangeForExtractDate(referenceExtractDate);
    return this.day.occupancy.occupancyChangeVersusPercentage(
      referenceOccupancy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOwnHotelRateChange(referenceChangeDays = this.referenceChangeDays) {
    return this.getCompareRate(referenceChangeDays)?.difference_absolute;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOwnHotelRateFormattedChange(
    referenceChangeDays = this.referenceChangeDays,
  ) {
    const compareRate = this.getCompareRate(referenceChangeDays);

    if (compareRate?.message === 'general.missing') {
      return this.intl.t('general.missing');
    }

    const ownHotelRateChange = this.getOwnHotelRateChange(referenceChangeDays);
    if (!ownHotelRateChange) {
      return '';
    }

    return this.hotelAndMarketService.formatRate(
      ownHotelRateChange,
      true,
      true,
      { maximumFractionDigits: 0, minimumFractionDigits: 0 },
      true,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOwnHotelRateForOta(ota: string) {
    return this.ownHotelRates.find((rate) => rate.ota === ota);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getReferenceChangeText(referenceChangeDays = this.referenceChangeDays) {
    if (
      this.hasOTBData &&
      (this.getSignificantOccupancyChange(referenceChangeDays) ||
        this.getSignificantMarketBenchmarkChange(referenceChangeDays))
    ) {
      return `${this.intl.t('rates.changes.change_tooltip')} ${this.intl.t(
        'general.daysago',
        {
          count: referenceChangeDays,
        },
      )}`;
    }

    // Show "No change vs X day(s) ago" when there are no changes
    return `${this.intl.t('ranking.no_change')} ${this.intl.t(
      'general.daysago',
      {
        count: referenceChangeDays,
      },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getReferenceExtractDate(referenceChangeDays: number) {
    return this.hotelAndMarketService
      .getLocalTime()
      .subtract(referenceChangeDays, 'day')
      .format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRentalOccupancyChange(referenceChangeDays = this.referenceChangeDays) {
    return this.rentalOccupancyForCurrentDay?.differenceForDay(
      referenceChangeDays,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRentalOccupancyFormattedChange(
    referenceChangeDays = this.referenceChangeDays,
  ) {
    return this.rentalOccupancyForCurrentDay?.formattedDifferenceForDay(
      referenceChangeDays,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRentalRateChange(referenceChangeDays = this.referenceChangeDays) {
    return this.rentalRateForCurrentDay?.differenceForDay(referenceChangeDays);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRentalRateFormattedChange(referenceChangeDays = this.referenceChangeDays) {
    return this.rentalRateForCurrentDay?.formattedDifferenceForDay(
      referenceChangeDays,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSignificantMarketBenchmarkChange(
    referenceChangeDays = this.referenceChangeDays,
  ) {
    const benchmarkChange = this.getBenchmarkChange(referenceChangeDays);

    if (
      !this.showOTBChanges ||
      benchmarkChange === null ||
      this.day.occupancy?.isBenchmarkMissing
    ) {
      return false;
    }

    return (
      benchmarkChange !== undefined &&
      (benchmarkChange >= SIGNIFICANT_DIFFERENCE ||
        benchmarkChange <= -SIGNIFICANT_DIFFERENCE)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSignificantOccupancyChange(
    referenceChangeDays = this.referenceChangeDays,
  ) {
    const occupancyChange = this.getOccupancyChange(referenceChangeDays);

    if (!this.showOTBChanges || occupancyChange === null) {
      return false;
    }

    return (
      occupancyChange >= SIGNIFICANT_DIFFERENCE ||
      occupancyChange <= -SIGNIFICANT_DIFFERENCE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSignificantShortTermRentalChange(
    referenceChangeDays = this.referenceChangeDays,
  ) {
    const rentalOccupancyChange =
      this.getRentalOccupancyChange(referenceChangeDays);
    const rentalRateChange = this.getRentalRateChange(referenceChangeDays);
    return (
      this.rentalOccupancyForCurrentDay &&
      this.rentalRateForCurrentDay &&
      rentalOccupancyChange !== undefined &&
      rentalRateChange !== undefined &&
      (Math.abs(rentalOccupancyChange) >=
        this.rentalOccupancyForCurrentDay.SIGNIFICANT_DIFFERENCE ||
        Math.abs(rentalRateChange) >=
          this.rentalRateForCurrentDay.SIGNIFICANT_DIFFERENCE)
    );
  }

  /* Resolves what a restriction row displays: the override when one is active,
     otherwise the effective value.

     COA-3693 TODO: remove the hasFailedPush branch once COA-3687 is resolved.
     BE still sends the failed override value when a push fails, so a failed push
     falls back to the effective value and skips the override. */
  private resolveOverriddenValue({
    effectiveValue,
    hasFailedPush,
    overrideValue,
  }: OverriddenValueOptions): number | null {
    if (hasFailedPush) {
      return effectiveValue;
    }

    return overrideValue ?? effectiveValue;
  }
}
